import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type Header,
} from '@tanstack/react-table';
import type { LucideIcon } from 'lucide-react';
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import {
  ROW_HOVER_BG,
  ROW_POINTER_HOVER_BG,
  ROW_SELECTED_BG,
  ROW_SELECTED_MARK_CLASS,
  STICKY_COL_DEFAULT_BG,
  STICKY_COL_HEADER_BG,
  STICKY_COL_POINTER_HOVER_BG,
  STICKY_COL_SELECTED_BG,
  TABLE_HEADER_BG,
  TABLE_HEADER_TEXT_CLASS,
} from '@/styles/dataTableBg';
import {
  ACTIONS_COLUMN_ID,
  BODY_CELL_PADDING_CLASS,
  BODY_ROW_CLASS,
  STICKY_TABLE_FOOTER_CLASS,
  STICKY_TABLE_HEADER_CLASS,
  TABLE_BORDER_MODEL_CLASS,
} from './dataTable.constants';
import { ColumnHeaderHint } from './ColumnHeaderHint';
import { DataTableLoading } from './DataTableLoading';
import { DataTableEmpty } from './DataTableEmpty';
import { getHeaderHint } from './headerHint';
import { InfiniteLoadingRow } from './InfiniteLoadingRow';
import { SortableColumnHeader } from './SortableColumnHeader';
import { TableScrollFrame } from './TableScrollFrame';
import { usePinnedColumnOffsets } from './usePinnedColumnOffsets';
import {
  ARIA_SORT_BY_DIRECTION,
  getNextTableSort,
  type SortDirection,
  type TableSort,
} from './tableSort';

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  isLoading?: boolean;
  stickyFirstColumn?: boolean;
  stickyColumnId?: string | undefined;
  fitContentColumnIds?: readonly string[] | undefined;
  skeletonRowCount?: number;
  className?: string;
  /** Grow to fill a flex parent, for a page whose table owns the height. */
  fillHeight?: boolean;
  emptyIcon?: LucideIcon;
  emptyMessage?: string;
  emptyHint?: string | undefined;
  emptyActions?: ReactNode;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadMoreError?: boolean;
  getRowClassName?: (row: TData) => string | undefined;
  isRowHighlighted?: ((row: TData) => boolean) | undefined;
  isRowPicked?: ((row: TData) => boolean) | undefined;
  onRowClick?: (row: TData) => void;
  groupedColumns?: readonly GroupedColumn<TData>[] | undefined;
  sort?: TableSort | undefined;
  onSortChange?: ((sort: TableSort | undefined) => void) | undefined;
  sortableColumnIds?: readonly string[] | undefined;
}

const SKELETON_ROW_DEFAULT = 15;
const STICKY_COLUMN_CLASS = 'lg:sticky lg:left-0';
const STICKY_COLUMN_HEADER_CLASS = cn(STICKY_COLUMN_CLASS, STICKY_COL_HEADER_BG);
const FIT_CONTENT_COLUMN_CLASS = 'w-px whitespace-nowrap';
const GROUPED_CELL_CLASS = 'cursor-default border-r border-border/60 bg-card align-top';
const GROUPED_CELL_LEFT_BORDER_CLASS = 'border-l';
const GROUPED_CELL_LABEL_CLASS = 'sticky top-14';

export interface GroupedColumn<TData> {
  columnId: string;
  getGroupKey: (row: TData) => unknown;
}

interface RowGroups {
  rowSpans: number[];
  groupStartIndexes: number[];
}

type RowGroupsByColumnId = ReadonlyMap<string, RowGroups>;

const NO_ROW_GROUPS: RowGroupsByColumnId = new Map();

const POINTER_HOVER_ATTRIBUTE = 'data-pointer-hover';

interface ClickedRow {
  rowId: string;
  highlightedRowIdAtClick: string | null;
}

function getSelectedRowId(
  clickedRow: ClickedRow | null,
  highlightedRowId: string | null,
): string | null {
  const isClickNewerThanHighlight = clickedRow?.highlightedRowIdAtClick === highlightedRowId;
  if (clickedRow === null || !isClickNewerThanHighlight) return highlightedRowId;
  return clickedRow.rowId;
}

interface RowBgState {
  isSelected: boolean;
  hasPointerHover: boolean;
}

function getRowBgClass({ isSelected, hasPointerHover }: RowBgState): string {
  if (isSelected) return cn(ROW_SELECTED_BG, ROW_SELECTED_MARK_CLASS);
  return hasPointerHover ? ROW_POINTER_HOVER_BG : ROW_HOVER_BG;
}

function getStickyCellBgClass({ isSelected, hasPointerHover }: RowBgState): string {
  if (isSelected) return STICKY_COL_SELECTED_BG;
  return hasPointerHover ? STICKY_COL_POINTER_HOVER_BG : STICKY_COL_DEFAULT_BG;
}

function findRowAtPointer(target: EventTarget, pointerY: number): HTMLTableRowElement | null {
  if (!(target instanceof Element)) return null;
  const cell = target.closest('td');
  const row = cell?.parentElement;
  if (!cell || !(row instanceof HTMLTableRowElement)) return null;
  if (cell.rowSpan <= 1) return row;

  let candidate: Element | null = row;
  for (let offset = 0; offset < cell.rowSpan; offset += 1) {
    if (!(candidate instanceof HTMLTableRowElement)) break;
    if (pointerY < candidate.getBoundingClientRect().bottom) return candidate;
    candidate = candidate.nextElementSibling;
  }
  return row;
}

function getPinnedColumnCount(
  columnIds: readonly string[],
  stickyColumnId: string | undefined,
  stickyFirstColumn: boolean | undefined,
): number {
  if (stickyColumnId !== undefined) return columnIds.indexOf(stickyColumnId) + 1;
  return stickyFirstColumn ? 1 : 0;
}

function renderHeaderContent<TData>(header: Header<TData, unknown>) {
  if (header.isPlaceholder) return null;
  return flexRender(header.column.columnDef.header, header.getContext());
}

function renderFooterContent<TData>(header: Header<TData, unknown>) {
  if (header.isPlaceholder) return null;
  return flexRender(header.column.columnDef.footer, header.getContext());
}

function groupConsecutiveRows<TData>(
  items: TData[],
  getGroupKey: (item: TData) => unknown,
  parentGroupStartIndexes: readonly number[],
): RowGroups {
  const rowSpans: number[] = [];
  const groupStartIndexes: number[] = [];
  let groupStartIndex = 0;
  let groupKey: unknown;
  items.forEach((item, index) => {
    const itemGroupKey = getGroupKey(item);
    const isParentGroupStart = parentGroupStartIndexes[index] === index;
    if (index === 0 || isParentGroupStart || itemGroupKey !== groupKey) {
      groupStartIndex = index;
      groupKey = itemGroupKey;
    }
    rowSpans[index] = 0;
    rowSpans[groupStartIndex] = (rowSpans[groupStartIndex] ?? 0) + 1;
    groupStartIndexes[index] = groupStartIndex;
  });
  return { rowSpans, groupStartIndexes };
}

function groupRowsByColumns<TData>(
  items: TData[],
  groupedColumns: readonly GroupedColumn<TData>[],
): RowGroupsByColumnId {
  const rowGroupsByColumnId = new Map<string, RowGroups>();
  let parentGroupStartIndexes: readonly number[] = [];
  for (const { columnId, getGroupKey } of groupedColumns) {
    const rowGroups = groupConsecutiveRows(items, getGroupKey, parentGroupStartIndexes);
    rowGroupsByColumnId.set(columnId, rowGroups);
    parentGroupStartIndexes = rowGroups.groupStartIndexes;
  }
  return rowGroupsByColumnId;
}

export function DataTable<TData>({
  data,
  columns,
  isLoading,
  stickyFirstColumn,
  stickyColumnId,
  fitContentColumnIds,
  skeletonRowCount = SKELETON_ROW_DEFAULT,
  className,
  fillHeight,
  emptyIcon,
  emptyMessage,
  emptyHint,
  emptyActions,
  onLoadMore,
  hasMore,
  isLoadingMore,
  loadMoreError,
  getRowClassName,
  isRowHighlighted,
  isRowPicked,
  onRowClick,
  groupedColumns,
  sort,
  onSortChange,
  sortableColumnIds,
}: DataTableProps<TData>) {
  const [clickedRow, setClickedRow] = useState<ClickedRow | null>(null);
  const pointerRowRef = useRef<HTMLTableRowElement | null>(null);
  const hasPointerHover = groupedColumns !== undefined;
  const hasRevealedHighlightedRowRef = useRef(false);
  const revealHighlightedRow = useCallback((row: HTMLTableRowElement | null) => {
    if (!row || hasRevealedHighlightedRowRef.current) return;
    hasRevealedHighlightedRowRef.current = true;
    row.scrollIntoView({ block: 'center' });
  }, []);
  const sentinelRef = useInfiniteScroll<HTMLTableRowElement>({
    onLoadMore,
    hasMore: hasMore && !loadMoreError,
    isLoadingMore,
  });

  useEffect(() => {
    setClickedRow(null);
  }, [data]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const pinnedColumnCount = getPinnedColumnCount(
    table.getVisibleLeafColumns().map((column) => column.id),
    stickyColumnId,
    stickyFirstColumn,
  );
  const pinnedColumnOffsets = usePinnedColumnOffsets(headerRowRef, pinnedColumnCount);

  function isStickyColumn(index: number): boolean {
    return index < pinnedColumnCount;
  }

  function getStickyColumnStyle(index: number): CSSProperties | undefined {
    if (index === 0 || !isStickyColumn(index)) return undefined;
    return { left: pinnedColumnOffsets[index] ?? 0 };
  }

  function isFitContentColumn(columnId: string): boolean {
    return columnId === ACTIONS_COLUMN_ID || (fitContentColumnIds?.includes(columnId) ?? false);
  }

  function getSortDirection(columnId: string): SortDirection | undefined {
    return sort?.columnId === columnId ? sort.direction : undefined;
  }

  function renderHeaderCell(header: Header<TData, unknown>) {
    const content = renderHeaderContent(header);
    const columnId = header.column.id;
    if (!onSortChange || !sortableColumnIds?.includes(columnId)) {
      const headerHint = getHeaderHint(header.column.columnDef.meta);
      if (!headerHint) return content;
      return <ColumnHeaderHint hint={headerHint}>{content}</ColumnHeaderHint>;
    }
    return (
      <SortableColumnHeader
        label={content}
        direction={getSortDirection(columnId)}
        onSort={() => onSortChange(getNextTableSort(sort, columnId))}
      />
    );
  }

  function getAriaSort(columnId: string) {
    if (!onSortChange || !sortableColumnIds?.includes(columnId)) return undefined;
    const direction = getSortDirection(columnId);
    return direction ? ARIA_SORT_BY_DIRECTION[direction] : 'none';
  }

  const rows = table.getRowModel().rows;
  const hasRows = rows.length > 0;
  const hasFooter = table
    .getAllLeafColumns()
    .some((column) => column.columnDef.footer !== undefined);
  const isFooterShown = hasFooter && (isLoading === true || hasRows);
  const highlightedRowId =
    rows.find((row) => isRowHighlighted?.(row.original) ?? false)?.id ?? null;
  const selectedRowId = getSelectedRowId(clickedRow, highlightedRowId);
  const rowGroupsByColumnId = useMemo(
    () => (groupedColumns ? groupRowsByColumns(data, groupedColumns) : NO_ROW_GROUPS),
    [data, groupedColumns],
  );
  const selectedRowIndex = rows.find((row) => row.id === selectedRowId)?.index;

  function isSelectedGroup(rowGroups: RowGroups, groupStartIndex: number): boolean {
    if (selectedRowIndex === undefined) return false;
    return rowGroups.groupStartIndexes[selectedRowIndex] === groupStartIndex;
  }

  function handleRowClick(rowId: string) {
    setClickedRow({ rowId, highlightedRowIdAtClick: highlightedRowId });
  }

  function handleGroupedCellClick(event: MouseEvent<HTMLTableCellElement>) {
    event.stopPropagation();
    const rowId = findRowAtPointer(event.target, event.clientY)?.dataset.rowId;
    if (rowId !== undefined) handleRowClick(rowId);
  }

  function markPointerRow(nextRow: HTMLTableRowElement | null) {
    const previousRow = pointerRowRef.current;
    if (previousRow === nextRow) return;
    previousRow?.removeAttribute(POINTER_HOVER_ATTRIBUTE);
    nextRow?.setAttribute(POINTER_HOVER_ATTRIBUTE, '');
    pointerRowRef.current = nextRow;
  }

  function handleBodyPointerMove(event: MouseEvent<HTMLTableSectionElement>) {
    markPointerRow(findRowAtPointer(event.target, event.clientY));
  }

  return (
    <TableScrollFrame className={cn(fillHeight === true && 'min-h-0 flex-1', className)}>
      <table
        className={cn(
          'min-w-full caption-bottom text-sm',
          TABLE_BORDER_MODEL_CLASS,
          !isLoading && !hasRows && 'h-full',
        )}
      >
        <TableHeader className={STICKY_TABLE_HEADER_CLASS}>
          {table.getHeaderGroups().map((headerGroup, groupIndex, headerGroups) => (
            <TableRow
              key={headerGroup.id}
              ref={groupIndex === headerGroups.length - 1 ? headerRowRef : undefined}
              className="hover:bg-transparent"
            >
              {headerGroup.headers.map((header, index) => (
                <TableHead
                  key={header.id}
                  aria-sort={getAriaSort(header.column.id)}
                  style={getStickyColumnStyle(index)}
                  className={cn(
                    'h-9 px-4 text-xs-plus font-semibold whitespace-nowrap',
                    TABLE_HEADER_BG,
                    TABLE_HEADER_TEXT_CLASS,
                    isStickyColumn(index) && cn(STICKY_COLUMN_HEADER_CLASS, 'z-30'),
                    isFitContentColumn(header.column.id) && FIT_CONTENT_COLUMN_CLASS,
                  )}
                >
                  {renderHeaderCell(header)}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody
          onMouseMove={hasPointerHover ? handleBodyPointerMove : undefined}
          onMouseLeave={hasPointerHover ? () => markPointerRow(null) : undefined}
        >
          {isLoading ? (
            <DataTableLoading
              columnCount={columns.length}
              rowCount={skeletonRowCount}
              columnIds={table.getVisibleLeafColumns().map((column) => column.id)}
            />
          ) : hasRows ? (
            <>
              {rows.map((row) => {
                const isHighlighted = isRowHighlighted?.(row.original) ?? false;
                const isPicked = isRowPicked?.(row.original) ?? false;
                const rowBgState: RowBgState = {
                  isSelected: selectedRowId === row.id || isPicked,
                  hasPointerHover,
                };
                const stickyCellClass = cn(STICKY_COLUMN_CLASS, getStickyCellBgClass(rowBgState));
                return (
                  <TableRow
                    key={row.id}
                    data-row-id={row.id}
                    ref={isHighlighted ? revealHighlightedRow : undefined}
                    onClick={() => {
                      handleRowClick(row.id);
                      onRowClick?.(row.original);
                    }}
                    className={cn(
                      'group/row',
                      BODY_ROW_CLASS,
                      onRowClick && 'cursor-pointer',
                      getRowBgClass(rowBgState),
                      getRowClassName?.(row.original),
                    )}
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      const rowGroups = rowGroupsByColumnId.get(cell.column.id);
                      const isGroupedCell = rowGroups !== undefined;
                      const rowSpan = rowGroups?.rowSpans[row.index];
                      if (rowSpan === 0) return null;

                      const content = flexRender(cell.column.columnDef.cell, cell.getContext());
                      return (
                        <TableCell
                          key={cell.id}
                          rowSpan={rowSpan}
                          onClick={isGroupedCell ? handleGroupedCellClick : undefined}
                          style={getStickyColumnStyle(index)}
                          className={cn(
                            BODY_CELL_PADDING_CLASS,
                            'whitespace-nowrap transition-colors',
                            isStickyColumn(index) && cn(stickyCellClass, 'z-10'),
                            isFitContentColumn(cell.column.id) && FIT_CONTENT_COLUMN_CLASS,
                            isGroupedCell && GROUPED_CELL_CLASS,
                            isGroupedCell && index > 0 && GROUPED_CELL_LEFT_BORDER_CLASS,
                            rowGroups && isSelectedGroup(rowGroups, row.index) && ROW_SELECTED_BG,
                          )}
                        >
                          {isGroupedCell ? (
                            <div className={GROUPED_CELL_LABEL_CLASS}>{content}</div>
                          ) : (
                            content
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {onLoadMore && (hasMore || loadMoreError) ? (
                <InfiniteLoadingRow
                  ref={sentinelRef}
                  columnCount={columns.length}
                  isLoading={isLoadingMore}
                  hasError={loadMoreError}
                  onRetry={onLoadMore}
                />
              ) : null}
            </>
          ) : (
            <DataTableEmpty
              columnCount={columns.length}
              icon={emptyIcon}
              message={emptyMessage}
              hint={emptyHint}
              actions={emptyActions}
            />
          )}
        </TableBody>
        {isFooterShown ? (
          <TableFooter className={STICKY_TABLE_FOOTER_CLASS}>
            {table.getFooterGroups().map((footerGroup) => (
              <TableRow key={footerGroup.id} className="hover:bg-transparent">
                {footerGroup.headers.map((header, index) => (
                  <TableCell
                    key={header.id}
                    style={getStickyColumnStyle(index)}
                    className={cn(
                      BODY_CELL_PADDING_CLASS,
                      'h-10 font-semibold whitespace-nowrap',
                      TABLE_HEADER_BG,
                      TABLE_HEADER_TEXT_CLASS,
                      isStickyColumn(index) && cn(STICKY_COLUMN_HEADER_CLASS, 'z-30'),
                      isFitContentColumn(header.column.id) && FIT_CONTENT_COLUMN_CLASS,
                    )}
                  >
                    {renderFooterContent(header)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableFooter>
        ) : null}
      </table>
    </TableScrollFrame>
  );
}
